import ReactHookFormInput from '@/components/ui/input/ReactHookFormInput';
import { Vessel } from '@/types/api';

import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { EditVesselForm } from '@/types/form';
import { useForm } from 'react-hook-form';

import clsx from 'clsx';

import toast from 'react-hot-toast';
import { useMutationUpdateVessel } from '@/api/queries/vessels';
import ReactHookFormSelect from '@/components/ui/select/ReactHookFormSelect';
import { useEffect } from 'react';
import useVesselsStore from '@/store/vessels';
import useAuthStore from '@/store/auth';
import Button from '@/components/ui/button';
import { OptionSelect } from '@/components/ui/select';
import { Lock } from 'lucide-react';

type Props = {
  vesselData?: Vessel | null;
  closeForm: () => void;
};

export default function VesselForm({ vesselData, closeForm }: Props) {
  const vesselStore = useVesselsStore((state) => state);
  const user = useAuthStore((state) => state.user);

  const isBlocked = Boolean(vesselData && user && (vesselData.customer_id !== user.id || !!vesselData.renter_id));

  const schema = Yup.object().shape({
    vessel_name: Yup.string().required('Vessel Name is Required'),
    ship_type: Yup.string().required('Ship Type is Required'),
    imo: Yup.string(),
    mmsi: Yup.string(),
    call_sign: Yup.string(),
    flag_state: Yup.string(),
  });

  const defaultValues: EditVesselForm = {
    vessel_name: vesselData?.vessel_name || '',
    ship_type: vesselData?.ship_type || '',
    imo: vesselData?.imo || '',
    mmsi: vesselData?.mmsi || '',
    call_sign: vesselData?.call_sign || '',
    flag_state: vesselData?.flag_state || '',
  };

  const {
    reset,
    register,
    control,
    setValue,
    watch,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<EditVesselForm>({
    //@ts-ignore
    defaultValues: defaultValues,
    mode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
  });

  const updateVessel = useMutationUpdateVessel();

  const handleSaveAccount = async () => {
    try {
      const validate = await trigger();
      if (!validate) {
        return;
      }

      const formData: EditVesselForm = getValues();

      const promiseSubmitData = new Promise(async (resolve, reject) => {
        try {
          const response = await updateVessel.mutateAsync({
            id: vesselData?.id || '',
            params: formData,
          });
          if (response) {
            vesselStore.updateVessel(vesselData?.id || '', response);
            resolve(response);
            closeForm();
          }
        } catch (e) {
          reject(e);
        }
      });

      toast.promise(promiseSubmitData, {
        loading: 'Saving...',
        success: 'Successfully saved!',
        error: (err) =>{
          console.error(err);
          return  `Could not save. Please try again! or contact support.`
        },
      });
    } catch (e) {
      closeForm();
      console.log(e);
    }
  };

  const optionsShip: OptionSelect[] = [
    { label: 'Tugboat', value: 'tugboat' },
    { label: 'High Speed', value: 'highspeed' },
    { label: 'Passenger', value: 'passenger' },
    { label: 'Cargo', value: 'cargo' },
    { label: 'Tanker', value: 'tanker' },
    { label: 'Fishing', value: 'fishing' },
    { label: 'Other', value: 'other' },
  ];

  return (
    <div className="relative p-10">
      {/* Blocking Overlay */}
      {isBlocked && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-gray-50/50 dark:bg-gray-900/60 backdrop-blur-[4px] rounded-xl p-6">
          <div className="bg-white dark:bg-gray-800 shadow-xl border border-gray-200/50 dark:border-gray-700 rounded-2xl p-6 flex flex-col items-center max-w-[320px] w-full transform -translate-y-4">
            <div className="h-14 w-14 bg-red-50 dark:bg-red-500/10 rounded-full flex items-center justify-center mb-4 ring-8 ring-red-50/50 dark:ring-red-500/5">
              <Lock className="w-6 h-6 text-red-500 dark:text-red-400" />
            </div>
            <h3 className="text-gray-900 dark:text-white font-semibold text-center mb-1.5">
              Akses Dibatasi
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-xs text-center mb-6 leading-relaxed">
              Informasi kapal ini hanya dapat diubah melalui akun utama.
            </p>
            <Button onClick={() => closeForm()} variant="outline" className="w-full justify-center">
              Kembali
            </Button>
          </div>
        </div>
      )}

      <div className={clsx("space-y-8 transition-opacity duration-300", isBlocked && "opacity-40 pointer-events-none select-none")}>
        <h1 className="text-2xl font-medium text-gray-800 dark:text-gray-200">
          {vesselData ? 'Edit Vessel' : 'Add New Vessel'}
        </h1>
        <div className="space-y-4">
          <div className="flex flex-col space-y-4">
            <ReactHookFormInput
              name="vessel_name"
              label="Vessel Name"
              control={control}
              errorMessage={errors?.vessel_name?.message}
            />

            <ReactHookFormSelect
              control={control}
              name="ship_type"
              label="Ship Type"
              value={watch('ship_type')}
              options={optionsShip}
              register={register}
              errorMessage={errors?.ship_type?.message}
            />

            <ReactHookFormInput
              name="imo"
              label="IMO"
              control={control}
              errorMessage={errors?.imo?.message}
            />

            <ReactHookFormInput
              name="mmsi"
              label="MMSI"
              control={control}
              errorMessage={errors?.mmsi?.message}
            />

            <ReactHookFormInput
              name="call_sign"
              label="Call Sign"
              control={control}
              errorMessage={errors?.call_sign?.message}
            />

            <ReactHookFormInput
              name="flag_state"
              label="Flag State"
              control={control}
              errorMessage={errors?.flag_state?.message}
            />
          </div>
          <div className="flex justify-between mt-20">
            <Button onClick={() => closeForm()} variant="outline" disabled={isBlocked}>
              Cancel
            </Button>

            <Button
              onClick={() => handleSaveAccount()}
              className={clsx({
                'bg-gray-600': updateVessel.isPending || isBlocked,
              })}
              disabled={updateVessel.isPending || isBlocked}
            >
              Save Vessel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
